import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken, BASE_URL } from './usePolicyChatStream';
import type { ChatMessage } from './MessageBubble';

export interface ConversationSummary {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

interface ConversationResponse {
  metadata: ConversationSummary;
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
  }>;
}

interface UseChatHistoryReturn {
  conversations: ConversationSummary[];
  setConversations: React.Dispatch<React.SetStateAction<ConversationSummary[]>>;
  isLoading: boolean;
  loadConversation: (id: string) => Promise<ChatMessage[]>;
  renameConversation: (id: string, title: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  refetch: () => void;
}

export function useChatHistory(): UseChatHistoryReturn {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchConversations = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${BASE_URL}/PolicyChat/conversations?skip=0&take=50`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (res.ok) {
        const data: ConversationSummary[] = await res.json();
        setConversations(data);
      }
    } catch {
      // Silent failure — sidebar will show empty list
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const loadConversation = useCallback(async (id: string): Promise<ChatMessage[]> => {
    const token = await getAuthToken();
    const res = await fetch(`${BASE_URL}/PolicyChat/conversations/${encodeURIComponent(id)}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) {
      throw new Error(`Failed to load conversation: ${res.status}`);
    }
    const data: ConversationResponse = await res.json();
    return data.messages.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
    }));
  }, []);

  const renameConversation = useCallback(async (id: string, title: string) => {
    const token = await getAuthToken();
    const res = await fetch(`${BASE_URL}/PolicyChat/conversations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ title }),
    });
    if (!res.ok) {
      throw new Error(`Failed to rename conversation: ${res.status}`);
    }
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, title } : c)),
    );
  }, []);

  const deleteConversation = useCallback(async (id: string) => {
    const token = await getAuthToken();
    const res = await fetch(`${BASE_URL}/PolicyChat/conversations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) {
      throw new Error(`Failed to delete conversation: ${res.status}`);
    }
    setConversations((prev) => prev.filter((c) => c.id !== id));
  }, []);

  return {
    conversations,
    setConversations,
    isLoading,
    loadConversation,
    renameConversation,
    deleteConversation,
    refetch: fetchConversations,
  };
}
